import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, map } from "rxjs";

import { environment } from "../../../../environments/environment";
import { SessionService } from "../../../core/services/session.service";
import {
  PaymentIntegration,
  PaymentIntegrationCreateDto,
  PaymentIntegrationFilterParams,
  PaymentIntegrationListResponse,
  PaymentIntegrationUpdateDto,
} from "../models/payment-integration.model";

/**
 * Payment Integrations Service
 * Focused service for payment integration CRUD operations
 * Wraps QPilot API calls with typed interfaces
 */
@Injectable({
  providedIn: "root",
})
export class PaymentIntegrationsService {
  private readonly http = inject(HttpClient);
  private readonly sessionService = inject(SessionService);

  private get baseUrl(): string {
    return environment.qpilotApiUrl + "/api";
  }

  /**
   * Get paginated list of payment integrations for a site
   * Note: API only supports pagination and sorting, not field-based filtering
   *
   * @param siteId - The site ID
   * @param filters - Filter/pagination parameters
   * @returns Observable of paginated response
   */
  getPaymentIntegrationsForSite(
    siteId: number,
    filters: Partial<PaymentIntegrationFilterParams> = {},
  ): Observable<PaymentIntegrationListResponse> {
    const url = `${this.baseUrl}/Sites/${siteId}/PaymentIntegrations`;

    // Build query params - API only supports these
    let params = new HttpParams();

    if (filters.page !== undefined) {
      params = params.set("page", filters.page.toString());
    }
    if (filters.pageSize !== undefined) {
      params = params.set("pageSize", filters.pageSize.toString());
    }
    if (filters.orderBy) {
      params = params.set("orderBy", filters.orderBy);
    }
    if (filters.order) {
      params = params.set("order", filters.order);
    }

    return this.http
      .get<{ Items: PaymentIntegration[]; TotalCount: number }>(url, { params })
      .pipe(
        map((response) => ({
          Items: response.Items || [],
          TotalCount: response.TotalCount || 0,
          Page: filters.page || 1,
          PageSize: filters.pageSize || 25,
        })),
      );
  }

  /**
   * Get a single payment integration by ID
   *
   * @param siteId - The site ID
   * @param id - The payment integration ID
   * @returns Observable of payment integration
   */
  getPaymentIntegrationById(
    siteId: number,
    id: number,
  ): Observable<PaymentIntegration> {
    const url = `${this.baseUrl}/Sites/${siteId}/PaymentIntegrations/${id}`;
    return this.http.get<PaymentIntegration>(url);
  }

  /**
   * Create a new payment integration
   *
   * @param siteId - The site ID
   * @param dto - The create DTO
   * @returns Observable of created payment integration
   */
  createPaymentIntegration(
    siteId: number,
    dto: PaymentIntegrationCreateDto,
  ): Observable<PaymentIntegration> {
    const url = `${this.baseUrl}/Sites/${siteId}/PaymentIntegrations`;
    return this.http.post<PaymentIntegration>(url, dto);
  }

  /**
   * Update an existing payment integration
   *
   * @param siteId - The site ID
   * @param id - The payment integration ID
   * @param dto - The update DTO
   * @returns Observable of updated payment integration
   */
  updatePaymentIntegration(
    siteId: number,
    id: number,
    dto: PaymentIntegrationUpdateDto,
  ): Observable<PaymentIntegration> {
    const url = `${this.baseUrl}/Sites/${siteId}/PaymentIntegrations/${id}`;
    return this.http.put<PaymentIntegration>(url, dto);
  }

  /**
   * Delete a payment integration
   *
   * @param siteId - The site ID
   * @param id - The payment integration ID
   * @returns Observable of void
   */
  deletePaymentIntegration(siteId: number, id: number): Observable<void> {
    const url = `${this.baseUrl}/Sites/${siteId}/PaymentIntegrations/${id}`;
    return this.http.delete<void>(url);
  }

  /**
   * Apply client-side filters to a list of payment integrations
   * Since API doesn't support field filtering, we filter after fetch
   *
   * @param items - The items to filter
   * @param filters - The filter parameters
   * @returns Filtered items
   */
  applyClientSideFilters(
    items: PaymentIntegration[],
    filters: Partial<PaymentIntegrationFilterParams>,
  ): PaymentIntegration[] {
    let filtered = [...items];

    // Filter by ID (partial string match - "596" finds 5966)
    if (
      filters.clientFilterId !== undefined &&
      filters.clientFilterId !== null
    ) {
      const idSearch = filters.clientFilterId.toString();
      filtered = filtered.filter((item) =>
        item.Id.toString().includes(idSearch),
      );
    }

    // Filter by provider/payment method type (case-insensitive partial match)
    if (filters.clientFilterProvider?.trim()) {
      const providerSearch = filters.clientFilterProvider.toLowerCase().trim();
      filtered = filtered.filter((item) =>
        item.PaymentMethodType?.toLowerCase().includes(providerSearch),
      );
    }

    // Filter by API account name (case-insensitive partial match)
    if (filters.clientFilterName?.trim()) {
      const nameSearch = filters.clientFilterName.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.ApiAccount?.toLowerCase().includes(nameSearch) ||
          item.PaymentMethodType?.toLowerCase().includes(nameSearch),
      );
    }

    // Filter by test mode
    if (
      filters.clientFilterTestMode !== undefined &&
      filters.clientFilterTestMode !== null
    ) {
      filtered = filtered.filter(
        (item) => item.TestMode === filters.clientFilterTestMode,
      );
    }

    return filtered;
  }
}
